import localeCurrency from 'locale-currency';
import accounting, { CurrencySettings } from 'accounting';
import currencies from './currencies.json';
import localeFormats from './localeFormats.json';

/** @private */
function isUndefined(val: string) {
    return typeof val === 'undefined';
}

/** @private */
function findCurrency(currencyCode: string) {
    return currencies[currencyCode as keyof typeof currencies];
}

export interface IFormatOptions {
    code?: string;
    locale?: string;
    symbol?: string;
    decimal?: string;
    thousand?: string;
    precision?: number;
    format?:
        | string
        | {
              pos: string;
              neg: string;
              zero: string;
          };
}

export interface Currency {
    code: string;
    decimalDigits: number;
    decimalSeparator: string;
    spaceBetweenAmountAndSymbol: boolean;
    symbol: string;
    symbolOnLeft: boolean;
    thousandsSeparator: string;
}

const defaultCurrency: Currency = {
    code: '',
    symbol: '',
    thousandsSeparator: ',',
    decimalSeparator: '.',
    symbolOnLeft: true,
    spaceBetweenAmountAndSymbol: false,
    decimalDigits: 2,
};

const defaultLocaleFormat = {};

const formatMapping = [
    {
        symbolOnLeft: true,
        spaceBetweenAmountAndSymbol: false,
        format: {
            pos: '%s%v',
            neg: '-%s%v',
            zero: '%s%v',
        },
    },
    {
        symbolOnLeft: true,
        spaceBetweenAmountAndSymbol: true,
        format: {
            pos: '%s %v',
            neg: '-%s %v',
            zero: '%s %v',
        },
    },
    {
        symbolOnLeft: false,
        spaceBetweenAmountAndSymbol: false,
        format: {
            pos: '%v%s',
            neg: '-%v%s',
            zero: '%v%s',
        },
    },
    {
        symbolOnLeft: false,
        spaceBetweenAmountAndSymbol: true,
        format: {
            pos: '%v %s',
            neg: '-%v %s',
            zero: '%v %s',
        },
    },
];

export function formatCurrency(value: number | string, options: IFormatOptions) {
    if (!options.code) return value as string;
    const code = options.code || (options.locale && localeCurrency.getCurrency(options.locale));
    const localeMatch = (/^([a-z]+)([_-]([a-z]+))?$/i.exec(options.locale!) ||
        []) as RegExpExecArray;
    const language = localeMatch[1];
    const region = localeMatch[3];
    const localeFormat = {
        ...defaultLocaleFormat,
        ...(localeFormats[language as keyof typeof localeFormats] || {}),
        ...(localeFormats[`${language}-${region}` as keyof typeof localeFormats] || {}),
    };
    const currency = {
        ...defaultCurrency,
        ...findCurrency(code!),
        ...localeFormat,
    };

    const { symbolOnLeft } = currency;
    const { spaceBetweenAmountAndSymbol } = currency;

    const { format } = formatMapping.filter((f) => {
        return (
            String(f.symbolOnLeft) === String(symbolOnLeft) &&
            String(f.spaceBetweenAmountAndSymbol) === String(spaceBetweenAmountAndSymbol)
        );
    })[0];

    const settings: CurrencySettings<string | { pos: string; neg: string; zero: string }> = {
        symbol: isUndefined(options.symbol!) ? currency.symbol : options.symbol,

        decimal: isUndefined(options.decimal!) ? currency.decimalSeparator : options.decimal,

        thousand: isUndefined(options.thousand!) ? currency.thousandsSeparator : options.thousand,

        precision:
            typeof options.precision === 'number' ? options.precision : currency.decimalDigits,

        format: ['string', 'object'].indexOf(typeof options.format) > -1 ? options.format : format,
    };

    // @ts-ignore
    return accounting.formatMoney(value, settings);
}

export function unformatCurrency(value: string, options: IFormatOptions) {
    const code = options.code || (options.locale && localeCurrency.getCurrency(options.locale));
    const localeFormat =
        localeFormats[options.locale! as keyof typeof localeFormats] || defaultLocaleFormat;
    const currency = { ...defaultCurrency, ...findCurrency(code!), ...localeFormat };
    const decimal = isUndefined(options.decimal!) ? currency.decimalSeparator : options.decimal;
    return accounting.unformat(value, decimal);
}
